import { FUNNEL_TYPE } from '../../enum/constants';
import { formatFullName } from '../../helpers';
import { ResolvedTaskLink, Task, TaskLink, TaskLinkType, TaskPriority, TaskStatus } from './tasks.types';

export const TASK_TITLE_MAX_LENGTH = 255;
/** The backend takes the reminder up to a year before the deadline */
export const TASK_REMINDER_MAX_MINUTES = 365 * 24 * 60;

/** Badge classes of the status of a task */
export const TASK_STATUS_CLASSES: Record<TaskStatus, string> = {
  NEW: 'bg-sky-50 text-sky-700',
  IN_PROGRESS: 'bg-amber-50 text-amber-700',
  REVIEW: 'bg-violet-50 text-violet-700',
  POSTPONED: 'bg-slate-100 text-slate-600',
  CANCELLED: 'bg-rose-50 text-rose-600',
  DONE: 'bg-emerald-50 text-emerald-700',
};

/** Badge classes and the icon of the priority of a task */
export const TASK_PRIORITY_STYLES: Record<TaskPriority, { badge: string; icon: string }> = {
  LOW: { badge: 'bg-slate-100 text-slate-500', icon: 'arrow_downward' },
  NORMAL: { badge: 'bg-indigo-50 text-indigo-700', icon: 'drag_handle' },
  HIGH: { badge: 'bg-rose-50 text-rose-700', icon: 'arrow_upward' },
};

export const TASK_LINK_ICONS: Record<TaskLinkType, string> = {
  card: 'view_kanban',
  contact: 'person',
  company: 'apartment',
};

/** A task is finished in these statuses: it is never overdue and sends no reminders */
export const isTaskFinished = (task: Pick<Task, 'status'>): boolean =>
  task.status === 'DONE' || task.status === 'CANCELLED';

// #region statuses

/**
 * Where a task can go from each status; finishing a task is not here, it depends on the review flag (see
 * `getAllowedTaskStatuses`). A finished task can be taken back to work. The backend allows exactly these moves.
 */
const TASK_STATUS_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  NEW: ['IN_PROGRESS', 'POSTPONED', 'CANCELLED'],
  IN_PROGRESS: ['POSTPONED', 'CANCELLED'],
  REVIEW: ['IN_PROGRESS', 'DONE', 'CANCELLED'],
  POSTPONED: ['IN_PROGRESS', 'CANCELLED'],
  DONE: ['IN_PROGRESS'],
  CANCELLED: ['IN_PROGRESS'],
};

/**
 * The statuses a task can be moved to. With the review flag the executor sends the task to review and the creator
 * accepts it, without the flag the executor closes the task on their own.
 */
export function getAllowedTaskStatuses(status: TaskStatus, requiresReview: boolean): TaskStatus[] {
  const allowed = [...TASK_STATUS_TRANSITIONS[status]];
  if (status === 'NEW' || status === 'IN_PROGRESS') allowed.push(requiresReview ? 'REVIEW' : 'DONE');
  return allowed;
}

/** A button that moves the task to another status */
export interface TaskStatusAction {
  status: TaskStatus;
  /** Translation key of the label */
  labelKey: string;
  icon: string;
  /** `primary` moves the work forward, `danger` drops the task */
  tone: 'primary' | 'default' | 'danger';
}

const TONE_ORDER: Record<TaskStatusAction['tone'], number> = { primary: 0, default: 1, danger: 2 };

function getStatusAction(from: TaskStatus, to: TaskStatus): TaskStatusAction {
  switch (to) {
    case 'IN_PROGRESS':
      return from === 'NEW'
        ? { status: to, labelKey: 'crm_tasks.actions.start', icon: 'play_arrow', tone: 'primary' }
        : { status: to, labelKey: 'crm_tasks.actions.return_to_work', icon: 'undo', tone: 'default' };
    case 'REVIEW':
      return { status: to, labelKey: 'crm_tasks.actions.send_to_review', icon: 'fact_check', tone: 'primary' };
    case 'DONE':
      return from === 'REVIEW'
        ? { status: to, labelKey: 'crm_tasks.actions.accept', icon: 'check_circle', tone: 'primary' }
        : { status: to, labelKey: 'crm_tasks.actions.complete', icon: 'check_circle', tone: 'primary' };
    case 'POSTPONED':
      return { status: to, labelKey: 'crm_tasks.actions.postpone', icon: 'schedule', tone: 'default' };
    default:
      return { status: to, labelKey: 'crm_tasks.actions.cancel', icon: 'cancel', tone: 'danger' };
  }
}

/**
 * The buttons of a task for the current user, the main ones first. Only the creator accepts a task that is on review
 * or returns it to work (the backend answers 403 to anyone else), so the others do not get those buttons.
 */
export function getTaskStatusActions(
  task: Pick<Task, 'status' | 'requires_review' | 'created_by'>,
  userId: string | null,
): TaskStatusAction[] {
  const creatorOnly: TaskStatus[] =
    task.status === 'REVIEW' && task.created_by !== userId ? ['DONE', 'IN_PROGRESS'] : [];

  return getAllowedTaskStatuses(task.status, task.requires_review)
    .filter((status) => !creatorOnly.includes(status))
    .map((status) => getStatusAction(task.status, status))
    .sort((a, b) => TONE_ORDER[a.tone] - TONE_ORDER[b.tone]);
}

// #endregion

// #region date and time pickers

const pad = (value: number): string => String(value).padStart(2, '0');

/** The last minute of the day of `now` in the local time, as a deadline that says "today" */
export function getEndOfDay(now: Date = new Date()): string {
  const end = new Date(now);
  end.setHours(23, 59, 0, 0);
  return end.toISOString();
}

/** Whether the moment is in the same local day as `now` */
export function isSameDay(iso: string | null | undefined, now: Date = new Date()): boolean {
  const moment = iso ? new Date(iso) : null;
  return (
    !!moment &&
    moment.getFullYear() === now.getFullYear() &&
    moment.getMonth() === now.getMonth() &&
    moment.getDate() === now.getDate()
  );
}

/** The nearest deadline first, the tasks without one last */
export function compareDeadlines(a: Pick<Task, 'deadline'>, b: Pick<Task, 'deadline'>): number {
  const left = a.deadline ? new Date(a.deadline).getTime() : Number.POSITIVE_INFINITY;
  const right = b.deadline ? new Date(b.deadline).getTime() : Number.POSITIVE_INFINITY;
  return left === right ? 0 : left < right ? -1 : 1;
}

/** The local date and time of a moment as the two pickers hold them: `yyyy-mm-dd` and `HH:mm` */
export function toPickerValues(iso: string | null | undefined): { date: string; time: string } {
  const instant = iso ? new Date(iso) : null;
  if (!instant || Number.isNaN(instant.getTime())) return { date: '', time: '' };

  return {
    date: `${instant.getFullYear()}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}`,
    time: `${pad(instant.getHours())}:${pad(instant.getMinutes())}`,
  };
}

/** The moment the two pickers hold, to the minute: none without a date, midnight without a time */
export function fromPickerValues(date: string | null | undefined, time: string | null | undefined): string | null {
  if (!date) return null;

  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = (time || '00:00').split(':').map(Number);
  const instant = new Date(year, month - 1, day, hours, minutes);
  return Number.isNaN(instant.getTime()) ? null : instant.toISOString();
}

// #endregion

// #region reminders

export const REMINDER_UNITS = ['minutes', 'hours', 'days'] as const;
export type ReminderUnit = (typeof REMINDER_UNITS)[number];

const REMINDER_UNIT_MINUTES: Record<ReminderUnit, number> = { minutes: 1, hours: 60, days: 24 * 60 };

export const toReminderMinutes = (value: number, unit: ReminderUnit): number => value * REMINDER_UNIT_MINUTES[unit];

/** 180 minutes are 3 hours: the biggest unit that divides the minutes without a remainder */
export function splitReminderMinutes(minutes: number): { value: number; unit: ReminderUnit } {
  if (minutes > 0 && minutes % REMINDER_UNIT_MINUTES.days === 0) {
    return { value: minutes / REMINDER_UNIT_MINUTES.days, unit: 'days' };
  }
  if (minutes > 0 && minutes % REMINDER_UNIT_MINUTES.hours === 0) {
    return { value: minutes / REMINDER_UNIT_MINUTES.hours, unit: 'hours' };
  }
  return { value: minutes, unit: 'minutes' };
}

// #endregion

// #region links and people

export const getLinkKey = (link: TaskLink): string => `${link.entity_type}:${link.entity_id}`;

export const toTaskLink = ({ entity_type, entity_id }: TaskLink): TaskLink => ({ entity_type, entity_id });

/** Translation key of what a link is: a card is a lead or a deal by the type of its funnel */
export function getLinkTypeKey(link: Pick<ResolvedTaskLink, 'entity_type' | 'funnelType'>): string {
  if (link.entity_type !== 'card') return `crm_tasks.link_type.${link.entity_type}`;

  switch (link.funnelType) {
    case FUNNEL_TYPE.LEAD:
      return 'crm_tasks.link_type.lead';
    case FUNNEL_TYPE.DEAL:
      return 'crm_tasks.link_type.deal';
    default:
      return 'crm_tasks.link_type.card';
  }
}

/** Takes from the groups in turns, one from each, until `limit` are taken: no group crowds out the others */
export function interleave<T>(groups: readonly (readonly T[])[], limit: number): T[] {
  const result: T[] = [];
  for (let index = 0; result.length < limit && groups.some((group) => index < group.length); index++) {
    for (const group of groups) {
      if (index < group.length && result.length < limit) result.push(group[index]);
    }
  }
  return result;
}

/** The same values in any order */
export function isSameSet(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((item) => right.has(item));
}

export function getPersonName(
  person: { first_name?: string | null; last_name?: string | null; email?: string | null } | null | undefined,
): string {
  return formatFullName(person?.first_name, person?.last_name) || person?.email || '';
}

// #endregion
